import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND } from "./constants";
import { AdminLeads } from "./AdminLeads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminManagers({ token }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => axios.get(`${API}/admin/homestay/managers`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setItems(r.data.items)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => {
    try { await axios.post(`${API}/admin/homestay/managers/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Manager activated"); load(); }
    catch { toast.error("Approve failed"); }
  };
  return (
    <div data-testid="admin-hs-managers" className="rounded-2xl border bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="font-bold text-sm" style={{ color: BRAND.green }}>Property managers ({items.length})</div>
      {items.map((m) => (
        <div key={m.id} data-testid={`admin-mgr-row-${m.id}`} className="flex items-center justify-between py-2 border-t text-sm" style={{ borderColor: BRAND.line }}>
          <span><b>{m.name}</b> <span className="text-xs" style={{ color: BRAND.muted }}>{m.email} · {m.free ? "free" : "extra ₹199"}</span></span>
          {m.status === "pending_payment"
            ? <button data-testid={`admin-mgr-approve-${m.id}`} onClick={() => approve(m.id)} className="chip text-xs" style={{ color: BRAND.green }}>✓ Activate</button>
            : <span className="text-xs font-bold" style={{ color: BRAND.green }}>active</span>}
        </div>
      ))}
    </div>
  );
}

function AdminSummaries({ token }) {
  const [d, setD] = useState({ items: [], date: "" });
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => axios.get(`${API}/admin/homestay/summaries`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setD(r.data)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    try { await axios.post(`${API}/admin/homestay/summaries/run`, {}, h); toast.success("Summaries generated"); load(); }
    catch { toast.error("Run failed"); }
  };
  return (
    <div data-testid="admin-hs-summaries" className="rounded-2xl border bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="flex items-center justify-between">
        <div className="font-bold text-sm" style={{ color: BRAND.green }}>Daily summaries · {d.date} (auto 11:30 AM IST)</div>
        <button data-testid="admin-hs-run-summaries" onClick={runNow} className="chip text-xs" style={{ color: BRAND.orange }}>Run now</button>
      </div>
      {d.items.length === 0 && <div className="text-xs mt-2" style={{ color: BRAND.muted }}>None generated yet today.</div>}
      {d.items.map((s) => (
        <div key={s.id} data-testid={`admin-summary-${s.property_id}`} className="flex items-center justify-between py-2 border-t text-sm" style={{ borderColor: BRAND.line }}>
          <span><b>{s.property_name}</b> <span className="text-xs" style={{ color: BRAND.muted }}>{s.stats.occupancy_pct}% · ₹{s.stats.collected_inr.toLocaleString("en-IN")} · {s.stats.checkouts_tomorrow.length} check-outs</span></span>
          <a href={s.wa_url_owner} target="_blank" rel="noreferrer" data-testid={`admin-summary-wa-${s.property_id}`} className="chip text-xs" style={{ color: "#16A34A" }}>Send to owner on WhatsApp</a>
        </div>
      ))}
    </div>
  );
}

function AdminReviews({ token }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => axios.get(`${API}/admin/homestay/reviews`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setItems(r.data.items)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (rv) => {
    try { await axios.patch(`${API}/admin/homestay/reviews/${rv.id}`, { hidden: !rv.hidden }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(rv.hidden ? "Review restored" : "Review hidden"); load(); }
    catch { toast.error("Action failed"); }
  };
  return (
    <div data-testid="admin-hs-reviews" className="rounded-2xl border bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="font-bold text-sm" style={{ color: BRAND.green }}>Guest reviews moderation ({items.length})</div>
      {items.map((rv) => (
        <div key={rv.id} data-testid={`admin-review-${rv.id}`} className="flex items-center justify-between gap-2 py-2 border-t text-sm" style={{ borderColor: BRAND.line, opacity: rv.hidden ? 0.55 : 1 }}>
          <span className="min-w-0"><b>{"★".repeat(rv.rating)}</b> {rv.property_name} · {rv.guest_name}<span className="block text-xs truncate" style={{ color: BRAND.muted }}>{rv.text || "(no text)"}</span></span>
          <button data-testid={`admin-review-toggle-${rv.id}`} onClick={() => toggle(rv)} className="chip text-xs shrink-0" style={{ color: rv.hidden ? BRAND.green : "#DC2626" }}>{rv.hidden ? "Unhide" : "Hide"}</button>
        </div>
      ))}
    </div>
  );
}

export function AdminHomeStay({ token }) {
  const [items, setItems] = useState([]);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => axios.get(`${API}/admin/homestay/properties`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setItems(r.data.items)).catch(() => toast.error("Could not load properties")), [token]);
  useEffect(() => { load(); }, [load]);

  const act = async (pid, path, body, msg) => {
    try { await axios.post(`${API}/admin/homestay/properties/${pid}/${path}`, body, h); toast.success(msg); load(); }
    catch { toast.error("Action failed"); }
  };

  return (
    <div data-testid="admin-homestay" className="space-y-3">
      <div className="text-sm" style={{ color: BRAND.muted }}>Approving a property starts its 7-day Featured trial. ₹999 UPI payments (purpose <b>homestay_featured</b>) confirmed in the UPI tab add 30 days automatically.</div>
      <div className="grid lg:grid-cols-2 gap-3"><AdminManagers token={token} /><AdminSummaries token={token} /><div className="lg:col-span-2"><AdminReviews token={token} /></div><div className="lg:col-span-2"><AdminLeads token={token} properties={items} /></div></div>
      {items.length === 0 && <div className="text-sm" data-testid="admin-homestay-empty">No properties yet.</div>}
      {items.map((p) => (
        <div key={p.id} data-testid={`admin-hs-row-${p.id}`} className="rounded-2xl border bg-white p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: BRAND.line }}>
          <div>
            <div className="font-bold" style={{ color: BRAND.green }}>{p.name} <span className="chip text-[10px]">{p.property_type}</span> <span className="chip text-[10px]" data-testid={`admin-hs-status-${p.id}`}>{p.status}</span></div>
            <div className="text-xs" style={{ color: BRAND.muted }}>{p.locality}, {p.city} · {p.phone} · {p.owner_email} · {p.beds} beds</div>
            <div className="text-xs mt-0.5" style={{ color: BRAND.orange }}>
              {p.access.featured ? `Featured till ${p.featured_until.slice(0, 10)}` : p.access.in_trial ? `Trial · ${p.access.trial_days_left}d left` : p.access.approved ? "Locked (trial ended)" : ""}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {p.status !== "approved" && <button data-testid={`admin-hs-approve-${p.id}`} onClick={() => act(p.id, "approve", {}, "Approved · trial started")} className="chip text-xs" style={{ color: BRAND.green }}>✓ Approve</button>}
            {p.status !== "rejected" && <button data-testid={`admin-hs-reject-${p.id}`} onClick={() => act(p.id, "reject", {}, "Rejected")} className="chip text-xs" style={{ color: "#DC2626" }}>Reject</button>}
            <button data-testid={`admin-hs-featured-${p.id}`} onClick={() => act(p.id, "featured", { days: 30 }, "+30 days Featured")} className="chip text-xs" style={{ color: BRAND.orange }}>+30d Featured</button>
          </div>
        </div>
      ))}
    </div>
  );
}
